import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaSpinner, FaTimes, FaCheck, FaEye, FaEyeSlash } from 'react-icons/fa';

import StarRating from './StarRating';
import { submitRating, validateRatingData } from '../../utils/restaurantApi';
import { RATING_CRITERIA_CONFIG } from '../../utils/ratingConstants';
import { showSuccessNotification, showErrorNotification, showInfoNotification, showWarningNotification } from '../../components/NotificationManager';
/**
 * Comprehensive Rating Form Component
 * Allows users to rate restaurants on multiple criteria
 */
const RatingForm = ({
  restaurantId,
  restaurantName = '',
  orderId = null,
  existingRating = null,
  onSubmit = () => {},
  onCancel = () => {},
  onSuccess = () => {},
  className = ''
}) => {
  // Form state
  const [ratings, setRatings] = useState({
    overall: 0,
    foodQuality: 0,
    deliverySpeed: 0,
    customerService: 0,
    valueForMoney: 0,
    packaging: 0
  });
  
  const [reviewText, setReviewText] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  // Character count for review
  const maxReviewLength = 500;
  const reviewCharCount = reviewText.length;
  const isReviewTooLong = reviewCharCount > maxReviewLength;

  // Initialize form with existing rating if editing
  useEffect(() => {
    if (existingRating) {
      // Handle both nested and flat data structures
      const ratingData = existingRating.ratings || existingRating;
      
      setRatings({
        overall: ratingData.overall || ratingData.overallRating || 0,
        foodQuality: ratingData.foodQuality || 0,
        deliverySpeed: ratingData.deliverySpeed || 0,
        customerService: ratingData.customerService || 0,
        valueForMoney: ratingData.valueForMoney || 0,
        packaging: ratingData.packaging || 0
      });
      
      // Handle review text - check multiple possible fields
      const reviewText = existingRating.reviewText || existingRating.review || '';
      setReviewText(reviewText);
      
      // Handle anonymous flag
      const isAnonymous = existingRating.isAnonymous || false;
      setIsAnonymous(isAnonymous);
      
      console.log('Pre-filling form with existing rating:', {
        existingRating,
        extractedRatings: {
          overall: ratingData.overall || ratingData.overallRating || 0,
          foodQuality: ratingData.foodQuality || 0,
          deliverySpeed: ratingData.deliverySpeed || 0,
          customerService: ratingData.customerService || 0,
          valueForMoney: ratingData.valueForMoney || 0,
          packaging: ratingData.packaging || 0
        },
        reviewText,
        isAnonymous
      });
    }
  }, [existingRating]);

  // Handle rating change for specific criteria
  const handleRatingChange = (criteria, value) => {
    setRatings(prev => ({
      ...prev,
      [criteria]: value
    }));
    
    // Clear errors when user starts rating
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  // Validate form before submission
  const validateForm = () => {
    const formData = {
      restaurantId: restaurantId,
      ratings,
      reviewText: reviewText.trim(),
      isAnonymous,
      orderId: orderId
    };

    const validation = validateRatingData(formData);
    
    if (!validation.isValid) {
      setErrors(validation.errors);
      return false;
    }

    setErrors([]);
    return true;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showErrorNotification('Please fix the form errors before submitting');
      return;
    }

    setIsSubmitting(true);

    try {
      const submissionData = {
        restaurantId: restaurantId,
        ratings,
        reviewText: reviewText.trim(),
        isAnonymous,
        orderId: orderId
      };

      const result = await submitRating(submissionData);
      
      showSuccessNotification(existingRating ? 'Rating updated successfully!' : 'Thank you for your rating!');
      
      // Call callbacks
      onSubmit(result);
      onSuccess(result);
      
      // Reset form if not editing
      if (!existingRating) {
        setRatings({
          overall: 0,
          foodQuality: 0,
          deliverySpeed: 0,
          customerService: 0,
          valueForMoney: 0,
          packaging: 0
        });
        setReviewText('');
        setIsAnonymous(false);
      }
      
    } catch (error) {
      console.error('Rating submission error:', error);
      showErrorNotification(error.message || 'Failed to submit rating. Please try again.');
      setErrors([error.message || 'Failed to submit rating']);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if form has been modified
  const hasChanges = () => {
    if (!existingRating) return ratings.overall > 0 || reviewText.trim().length > 0;
    
    // Handle both nested and flat data structures for comparison
    const originalRatings = existingRating.ratings || existingRating;
    const originalReviewText = existingRating.reviewText || existingRating.review || '';
    const originalIsAnonymous = existingRating.isAnonymous || false;
    
    return (
      ratings.overall !== (originalRatings.overall || originalRatings.overallRating || 0) ||
      ratings.foodQuality !== (originalRatings.foodQuality || 0) ||
      ratings.deliverySpeed !== (originalRatings.deliverySpeed || 0) ||
      ratings.customerService !== (originalRatings.customerService || 0) ||
      ratings.valueForMoney !== (originalRatings.valueForMoney || 0) ||
      ratings.packaging !== (originalRatings.packaging || 0) ||
      reviewText.trim() !== originalReviewText.trim() ||
      isAnonymous !== originalIsAnonymous
    );
  };

  const canSubmit = ratings.overall > 0 && !isSubmitting && !isReviewTooLong;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-2xl shadow-xl border border-orange-100 overflow-hidden ${className}`}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-50 to-red-50 px-6 py-4 border-b border-orange-100">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-gray-800">
              {existingRating ? 'Edit Your Rating' : 'Rate Your Experience'}
            </h3>
            {restaurantName && (
              <p className="text-gray-600 mt-1">
                How was your experience with <span className="font-medium text-orange-600">{restaurantName}</span>?
              </p>
            )}
          </div>
          <button
            onClick={onCancel}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-full transition-colors"
            aria-label="Close rating form"
          >
            <FaTimes />
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-6">
        {/* Error Display */}
        {errors.length > 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg"
          >
            <h4 className="text-red-800 font-medium mb-2"></h4>
            <ul className="text-red-600 text-sm space-y-1">
              {errors.map((error, index) => (
                <li key={index}>• {error}</li>
              ))}
            </ul>
          </motion.div>
        )}

        {/* Rating Criteria */}
        <div className="space-y-6 mb-6">
          {Object.entries(RATING_CRITERIA_CONFIG).map(([key, config]) => (
            <div key={key} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{config.icon}</span>
                <div>
                  <label className="font-medium text-gray-800 block">
                    {config.label}
                    {config.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <p className="text-sm text-gray-500">{config.description}</p>
                </div>
              </div>
              <div className="flex-shrink-0">
                <StarRating
                  value={ratings[key]}
                  onChange={(value) => handleRatingChange(key, value)}
                  size="lg"
                  showValue={true}
                  label=""
                />
              </div>
            </div>
          ))}
        </div>

        {/* Review Text */}
        <div className="mb-6">
          <label className="block font-medium text-gray-800 mb-2">
            Share Your Experience (Optional)
          </label>
          <div className="relative">
            <textarea
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              placeholder="Tell others about your experience with this restaurant..."
              className={`
                w-full px-4 py-3 border-2 rounded-xl resize-none transition-colors
                ${isReviewTooLong 
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                  : 'border-gray-200 focus:border-orange-300 focus:ring-orange-100'
                }
                focus:outline-none focus:ring-4
              `}
              rows="4"
              maxLength={maxReviewLength + 50} // Allow slight overflow for better UX
            />
            <div className={`
              absolute bottom-3 right-3 text-sm
              ${isReviewTooLong ? 'text-red-500' : 'text-gray-400'}
            `}>
              {reviewCharCount}/{maxReviewLength}
            </div>
          </div>
          {isReviewTooLong && (
            <p className="text-red-500 text-sm mt-1">
              Review text is too long. Please keep it under {maxReviewLength} characters.
            </p>
          )}
        </div>

        {/* Anonymous Option */}
        <div className="mb-6">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={isAnonymous}
              onChange={(e) => setIsAnonymous(e.target.checked)}
              className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-200"
            />
            <div className="flex items-center gap-2">
              {isAnonymous ? <FaEyeSlash className="text-gray-500" /> : <FaEye className="text-gray-500" />}
              <span className="text-gray-700">Submit anonymously</span>
            </div>
          </label>
          <p className="text-sm text-gray-500 ml-7 mt-1">
            Your name will be hidden from other users
          </p>
        </div>

        {/* Preview Toggle */}
        {(ratings.overall > 0 || reviewText.trim()) && (
          <div className="mb-6">
            <button
              type="button"
              onClick={() => setShowPreview(!showPreview)}
              className="flex items-center gap-2 text-orange-600 hover:text-orange-700 font-medium"
            >
              {showPreview ? 'Hide Preview' : 'Show Preview'}
            </button>
          </div>
        )}

        {/* Preview */}
        {showPreview && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-200"
          >
            <h4 className="font-medium text-gray-800 mb-3">Preview:</h4>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <StarRating value={ratings.overall} readonly size="sm" showValue />
                <span className="text-sm text-gray-600">Overall Rating</span>
              </div>
              {reviewText.trim() && (
                <p className="text-gray-700 text-sm italic">"{reviewText.trim()}"</p>
              )}
              <p className="text-xs text-gray-500">
                By: {isAnonymous ? 'Anonymous' : 'Your Name'}
              </p>
            </div>
          </motion.div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 text-gray-600 hover:text-gray-800 font-medium transition-colors"
          >
            Cancel
          </button>
          
          <div className="flex items-center gap-3">
            {hasChanges() && !existingRating && (
              <span className="text-sm text-gray-500">Unsaved changes</span>
            )}
            
            <motion.button
              type="submit"
              disabled={!canSubmit}
              whileHover={canSubmit ? { scale: 1.02 } : {}}
              whileTap={canSubmit ? { scale: 0.98 } : {}}
              className={`
                px-8 py-3 rounded-xl font-bold text-white transition-all duration-300 flex items-center gap-2
                ${canSubmit
                  ? 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 shadow-lg hover:shadow-xl'
                  : 'bg-gray-300 cursor-not-allowed'
                }
              `}
            >
              {isSubmitting ? (
                <>
                  <FaSpinner className="animate-spin" />
                  {existingRating ? 'Updating...' : 'Submitting...'}
                </>
              ) : (
                <>
                  <FaCheck />
                  {existingRating ? 'Update Rating' : 'Submit Rating'}
                </>
              )}
            </motion.button>
          </div>
        </div>
      </form>
    </motion.div>
  );
};

export default RatingForm;